import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, ShieldCheck, Sparkles, Crown, Building2, CircleDashed, Send, RefreshCw } from "lucide-react";

/**
 * AdminVerificationDrive — Founders' Verification Drive control panel.
 * Shows the conversion funnel (sent → KYC → phone → practitioner+ →
 * reward) and provides Dry-Run + Send Drive actions for admins.
 */
export default function AdminVerificationDrive() {
  const [stats, setStats] = useState(null);
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/verification-drive/stats");
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stats");
    }
  };

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/verification-drive/send?dryRun=true");
      setDry(data);
      toast.success(`${data.wouldSend} eligible recipients`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    } finally { setBusy(false); }
  };

  const sendLive = async () => {
    if (confirmText.trim().toUpperCase() !== "SEND") {
      toast.error('Type SEND to confirm');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/verification-drive/send");
      toast.success(`Sent to ${data.sent} providers`);
      setSendOpen(false);
      setConfirmText("");
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const runFollowups = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/verification-drive/run-followups");
      toast.success(`${data.followupsSent} day-7 + ${data.lastChanceSent} day-30 nudges sent`);
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Follow-up sweep failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { loadStats(); }, []);
  if (!stats) return <div className="text-foreground/55">Loading…</div>;

  const FunnelRow = ({ icon: Icon, label, value, color = "text-foreground/85" }) => (
    <div className="flex items-center justify-between py-2 border-b border-secondary/10 last:border-0">
      <div className={`inline-flex items-center gap-2 ${color}`}>
        <Icon size={14} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      <span className="font-serif text-xl">{value}</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-verification-drive">
      <div>
        <div className="label-mystic">Campaign</div>
        <h2 className="font-serif text-3xl mt-1">Founders' Verification Drive</h2>
        <p className="text-foreground/70 mt-2 max-w-2xl">
          Sends every active-subscribed provider an email offering a free 30-day
          subscription extension if they complete KYC and add lineage in 60 days.
          The reward issues automatically via the background sweep when the
          recipient hits Verified Practitioner or better.
        </p>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="parchment p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mystic">Conversion Funnel</div>
            <button onClick={loadStats} className="text-[11px] uppercase tracking-[0.16em] text-foreground/55 hover:text-secondary inline-flex items-center gap-1" data-testid="drive-refresh">
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
          <FunnelRow icon={Mail} label="Emails sent" value={stats.sent} />
          <FunnelRow icon={Mail} label="Day-7 nudges sent" value={stats.followupSent || 0} color="text-foreground/65" />
          <FunnelRow icon={Mail} label="Day-30 last-chance sent" value={stats.lastChanceSent || 0} color="text-orange-300" />
          <FunnelRow icon={CircleDashed} label="KYC started" value={stats.kycStarted} />
          <FunnelRow icon={ShieldCheck} label="KYC approved" value={stats.kycApproved} />
          <FunnelRow icon={CircleDashed} label="Phone added" value={stats.phoneAdded} />
          <FunnelRow icon={ShieldCheck} label="→ Practitioner+" value={stats.convertedToPractitionerPlus} color="text-secondary" />
          <FunnelRow icon={Sparkles} label="Reward issued (30d sub credit)" value={stats.rewardIssued} color="text-violet-200" />
          <div className="mt-3 pt-3 border-t border-secondary/15 flex items-center justify-between text-xs">
            <span className="text-foreground/55">Sent → Practitioner+ rate</span>
            <span className="text-secondary font-serif text-lg">{stats.conversionRatePct}%</span>
          </div>
        </div>

        <div className="parchment p-5">
          <div className="label-mystic mb-3">Tier Distribution (recipients only)</div>
          <FunnelRow icon={CircleDashed} label="None" value={stats.byTier?.none || 0} />
          <FunnelRow icon={ShieldCheck} label="Practitioner" value={stats.byTier?.practitioner || 0} color="text-secondary" />
          <FunnelRow icon={Building2} label="Temple" value={stats.byTier?.temple || 0} color="text-blue-300" />
          <FunnelRow icon={Sparkles} label="Initiate" value={stats.byTier?.initiate || 0} color="text-violet-300" />
          <FunnelRow icon={Crown} label="Elder" value={stats.byTier?.elder || 0} color="text-secondary" />
        </div>
      </div>

      {/* Actions */}
      <div className="parchment p-5 space-y-4">
        <div className="label-mystic">Actions</div>
        <div className="flex flex-wrap gap-3">
          <button onClick={runDryRun} disabled={busy}
            className="border border-secondary/40 text-secondary px-4 py-2 rounded-sm uppercase tracking-wider text-xs hover:bg-secondary/10 disabled:opacity-50"
            data-testid="drive-dryrun-btn">
            {busy ? "..." : "Dry Run"}
          </button>
          <button onClick={runFollowups} disabled={busy}
            className="border border-violet-300/40 text-violet-200 px-4 py-2 rounded-sm uppercase tracking-wider text-xs hover:bg-violet-300/10 disabled:opacity-50"
            data-testid="drive-followup-btn">
            Run day-7 / day-30 drips
          </button>
          <button onClick={() => setSendOpen(true)} disabled={busy}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="drive-send-open-btn">
            <Send size={12} /> Send Drive Email
          </button>
        </div>

        {dry && (
          <div className="border border-secondary/20 rounded-sm p-4 space-y-2 text-sm" data-testid="drive-dryrun-result">
            <div className="text-foreground/75">
              <b>Would send:</b> <span className="text-secondary text-lg font-serif">{dry.wouldSend}</span>
            </div>
            <div className="text-foreground/60 text-xs space-y-0.5">
              <div>Skipped (already verified Initiate/Elder): {dry.skipped.alreadyVerified}</div>
              <div>Skipped (already received drive email): {dry.skipped.alreadySent}</div>
              <div>Skipped (no email on file): {dry.skipped.noEmail}</div>
            </div>
            {dry.sample?.length > 0 && (
              <div className="pt-2 border-t border-secondary/10">
                <div className="label-mystic mb-1">Sample (first 10)</div>
                <div className="text-xs text-foreground/65 space-y-0.5 max-h-40 overflow-y-auto">
                  {dry.sample.map((s) => (
                    <div key={s.userId}>• {s.name} — <span className="text-foreground/50">{s.email}</span> · tier {s.currentTier}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send confirmation modal */}
      {sendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm" data-testid="drive-confirm-modal">
          <div className="bg-card border border-secondary/30 rounded-sm p-6 w-full max-w-md space-y-4 mx-4">
            <div>
              <div className="label-mystic">Confirm send</div>
              <h2 className="font-serif text-2xl mt-1">Send to all eligible providers</h2>
            </div>
            <p className="text-sm text-foreground/70">
              This sends real email + in-app notifications and marks each recipient as
              "drive-sent" (they won't get duplicates). The reward sweep auto-credits
              30 days of subscription when they reach Verified Practitioner+.
            </p>
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">Type SEND to confirm</div>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
                data-testid="drive-confirm-input" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setSendOpen(false); setConfirmText(""); }}
                className="text-foreground/60 px-4 py-2 text-xs uppercase tracking-wider"
                data-testid="drive-confirm-cancel">Cancel</button>
              <button onClick={sendLive} disabled={busy}
                className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
                data-testid="drive-confirm-send">{busy ? "Sending..." : "Send Now"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
